/**
 * Display formatters for the public-profile header stats.
 *
 * The repo has no compact-number utility, so `formatCompactCount` is added here.
 * Counts with thousands separators reuse the native `toLocaleString` convention
 * already used elsewhere (e.g. `src/features/examples/components/isr-example.tsx`).
 */

/**
 * Normalize an i18n locale (`en_US`) to a BCP-47 tag (`en-US`) that the `Intl`
 * APIs accept. Returns `undefined` for a falsy locale so `Intl` falls back to
 * the runtime default.
 */
function toBcp47(locale?: string): string | undefined {
    return locale ? locale.replace(/_/g, '-') : undefined;
}

/**
 * Compact learner count with a trailing "+" once it reaches 1,000.
 * Rounds DOWN to the nearest thousand so the "+" reads as "at least":
 *   10252 → "10K+"   1500 → "1K+"   999 → "999"
 */
export function formatCompactCount(value: number, locale?: string): string {
    const tag = toBcp47(locale);
    if (value < 1000) {
        return value.toLocaleString(tag);
    }
    const floored = Math.floor(value / 1000) * 1000;
    const compact = new Intl.NumberFormat(tag, {
        notation: 'compact',
        maximumFractionDigits: 0,
    }).format(floored);
    return `${compact}+`;
}

/** Average rating to a single decimal place: 4.7 → "4.7". */
export function formatRating(value: number, locale?: string): string {
    return new Intl.NumberFormat(toBcp47(locale), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(value);
}

/** Plain count with thousands separators: 2431 → "2,431". */
export function formatCount(value: number, locale?: string): string {
    return value.toLocaleString(toBcp47(locale));
}
